import { useEffect, useState } from 'react';
import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Timestamp,
} from 'firebase/firestore';
import { ulid } from 'ulid';
import { getDb, getStorageClient } from '@talk2nick/shared';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { prepareImage } from './media';
import {
  checkClaim,
  expiresAtFromTtl,
  isValidGmail,
  normalizeEmail,
  roleFromTicket,
  type ClaimCheck,
  type InvitationDoc,
  type InviteRequestDoc,
  type TicketDoc,
  type TicketRole,
} from '@talk2nick/core';

export type { InviteRequestDoc };

/**
 * 방 접근 티켓 데이터 레이어 (PLAN 0-B 변경 9장, 2026-09-30 확정).
 *
 * 친구 요청을 대체하고 **방 단위 접근권**을 관리한다. 티켓 1장 = 1명(이메일 고정) 1회 사용.
 * 순수 판정 로직은 `@talk2nick/core/tickets`가 갖고, 이 파일은 Firestore 읽기/쓰기만 담당한다.
 *
 * Rules가 강제하는 쓰기 순서 (배치로 묶을 수 없다 — 규칙의 get()이 배치 이전 상태를 본다):
 *  1) `tickets/{token}` 갱신 (pending → used)
 *  2) `members/{uid}` 생성 — 티켓 경유로는 owner를 얻을 수 없다
 *  3) `convos/{cid}` 갱신 — memberIds에 본인 추가
 */

/** 티켓 token 바이트 수 (32바이트 = 256비트 — 추측 불가) */
const TOKEN_BYTES = 32;

export interface IssueTicketInput {
  email: string;
  role: TicketRole;
  /** 0 = 무제한 */
  ttlSeconds: number;
  note?: string;
  convoTitle?: string;
}

export interface IssuedTicket {
  token: string;
  ticket: TicketDoc;
  /** 티켓이 달린 방 */
  convoId: string;
  /** 실제로 공유할 링크 */
  link: string;
}

/** Rules의 권한 거부까지 뭉뚱그려 이 오류로 바꾼다 — 열거 방지와 무관하게 "없는 티켓"으로 보인다 */
export const TICKET_NOT_FOUND = 'ticket-not-found';

export class TicketError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = 'TicketError';
  }
}

/** base64url — 문서 ID에 쓸 수 있는 URL 안전 문자만 남긴다 */
function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** CSPRNG 시크릿 — Math.random은 절대 쓰지 않는다 (티켓이 곧 출입권) */
function randomSecret(): string {
  const bytes = new Uint8Array(TOKEN_BYTES);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

/**
 * token = `convoId~시크릿`.
 *
 * 링크는 `/join/{token}` 하나만 갖는다. 방 ID를 경로에 따로 노출하면 DM의 방 ID가
 * `uidA_uidB`이라 상대 uid가 새는데, token 안에 넣으면 조용히 처리된다.
 * 구분자 `~`는 Firestore 문서 ID에 허용되고 DM 키(uid_uid)에는 나오지 않는다.
 */
export function makeToken(convoId: string): string {
  return `${convoId}~${randomSecret()}`;
}

/** token에서 방 ID와 시크릿을 분리한다. 형식이 아니면 null */
export function parseToken(token: string): { convoId: string; secret: string } | null {
  const at = token.indexOf('~');
  if (at <= 0 || at === token.length - 1) return null;
  return { convoId: token.slice(0, at), secret: token.slice(at + 1) };
}

function ticketRef(convoId: string, token: string) {
  return doc(getDb(), 'convos', convoId, 'tickets', token);
}

/** issueTicket 을 감싸 호출자가 어느 단계에서 실패했는지 알 수 있게 한다 */
export class TicketIssueError extends Error {
  constructor(
    readonly stage: 'room' | 'ticket',
    // `Error`의 `cause`와 이름이 겹쳐 base 멤버로 취급된다 — override가 필요하다.
    override readonly cause: unknown,
  ) {
    super(`ticket-issue-failed-at-${stage}`);
    this.name = 'TicketIssueError';
  }
}

/** Firestore 문서 → core 타입 (Timestamp → epoch ms) */
function toTicketDoc(id: string, data: Record<string, unknown>): TicketDoc {
  const ms = (value: unknown): number | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'number') return value;
    const t = value as Timestamp;
    return typeof t?.toDate === 'function' ? t.toDate().getTime() : undefined;
  };
  const ticket: TicketDoc = {
    token: id,
    role: data.role === 'admin' ? 'admin' : 'member',
    status: (data.status as TicketDoc['status']) ?? 'pending',
    createdBy: (data.createdBy as string) ?? '',
    createdAt: ms(data.createdAt) ?? 0,
  };
  if (data.type === 'qr') ticket.type = 'qr';
  if (typeof data.emailLower === 'string' && data.emailLower) ticket.emailLower = data.emailLower;
  if (typeof data.note === 'string' && data.note) ticket.note = data.note;
  const usedBy = data.usedBy as string | undefined;
  if (usedBy) ticket.usedBy = usedBy;
  const usedAt = ms(data.usedAt);
  if (usedAt !== undefined) ticket.usedAt = usedAt;
  const expiresAt = ms(data.expiresAt);
  if (expiresAt !== undefined) ticket.expiresAt = expiresAt;
  const revokedAt = ms(data.revokedAt);
  if (revokedAt !== undefined) ticket.revokedAt = revokedAt;
  return ticket;
}

/** 발급 링크 — 서비스워커가 이미 /join/* 을 네트워크 우선으로 처리하도록 등록돼 있다 */
export function ticketLink(token: string, email?: string | null): string {
  const base = typeof location === 'undefined' ? '' : location.origin;
  const query = email ? `?email=${encodeURIComponent(email)}` : '';
  return `${base}/join/${token}${query}`;
}

/** 링크 복사 — 클립보드를 못 여는 환경(구버전 WebView)에서는 예외를 올린다 */
export async function copyTicketLink(link: string): Promise<void> {
  await navigator.clipboard.writeText(link);
}

/** 시스템 공유 시트 — 미지원 환경이면 false (호출쪽은 복사로 대체) */
export async function shareTicketLink(link: string): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.share) return false;
  try {
    await navigator.share({ url: link });
    return true;
  } catch {
    // 사용자가 공유를 취소한 경우도 여기에 걸린다
    return false;
  }
}

// ── 발급 ──────────────────────────────────────────────────────────────

/**
 * 티켓 1장 발급. 방장/부방장만 (Rules가 isAdmin을 요구한다).
 *
 * 이메일이 비었으면 여기서 막는다 — Rules가 `size() > 0`을 걸지만 클라이언트에서
 * 먼저 거부해야 사용자가 규칙 위반을 이해할 수 있다.
 */
export async function issueTicket(
  convoId: string,
  issuerUid: string,
  input: IssueTicketInput,
): Promise<IssuedTicket> {
  const emailLower = normalizeEmail(input.email);
  if (!isValidGmail(emailLower)) throw new TicketError('invalid-email');

  const now = Date.now();
  const token = makeToken(convoId);
  const expiresAt = expiresAtFromTtl(input.ttlSeconds, now);
  const note = input.note?.trim();

  const ticketData: Record<string, unknown> = {
    emailLower,
    role: input.role,
    status: 'pending',
    createdBy: issuerUid,
    createdAt: serverTimestamp(),
  };
  if (note) ticketData.note = note;
  if (expiresAt !== undefined) ticketData.expiresAt = new Date(expiresAt);

  const inviteData: Record<string, unknown> = {
    token,
    convoId,
    convoTitle: input.convoTitle || (input.role === 'admin' ? '관리자 초대' : '대화방'),
    emailLower,
    role: input.role,
    status: 'pending',
    createdBy: issuerUid,
    createdByName: 'nIcK',
    createdByEmail: 'heroyik@gmail.com',
    createdAt: serverTimestamp(),
  };
  if (note) inviteData.note = note;
  if (expiresAt !== undefined) inviteData.expiresAt = new Date(expiresAt);

  await Promise.all([
    setDoc(ticketRef(convoId, token), ticketData),
    setDoc(doc(getDb(), 'invitations', token), inviteData).catch((err: unknown) => {
      console.warn('[talk2nIcK] invitations 인덱스 생성 실패 (티켓은 정상 발급됨):', err);
    }),
  ]);

  const ticket: TicketDoc = {
    token,
    emailLower,
    role: input.role,
    status: 'pending',
    createdBy: issuerUid,
    createdAt: now,
    ...(note ? { note } : {}),
    ...(expiresAt !== undefined ? { expiresAt } : {}),
  };

  return { token, ticket, convoId, link: ticketLink(token, emailLower) };
}

export interface IssueQrTicketInput {
  role?: TicketRole;
  ttlSeconds?: number;
  note?: string;
  convoTitle?: string;
}

/**
 * 이메일 지정 없이 모바일 카메라로 스캔해 즉시 참여하는 QR 초대장 발급.
 * 방장/Admin 전용.
 */
export async function issueQrTicket(
  convoId: string,
  issuerUid: string,
  input?: IssueQrTicketInput,
): Promise<IssuedTicket> {
  const now = Date.now();
  const token = makeToken(convoId);
  const ttl = input?.ttlSeconds ?? 86_400; // 기본 24시간
  const expiresAt = expiresAtFromTtl(ttl, now);
  const note = input?.note?.trim() || 'QR 즉시 초대장';
  const role: TicketRole = input?.role ?? 'member';

  const ticketData: Record<string, unknown> = {
    type: 'qr',
    role,
    status: 'pending',
    createdBy: issuerUid,
    createdAt: serverTimestamp(),
  };
  if (note) ticketData.note = note;
  if (expiresAt !== undefined) ticketData.expiresAt = new Date(expiresAt);

  const inviteData: Record<string, unknown> = {
    token,
    convoId,
    type: 'qr',
    convoTitle: input?.convoTitle || '대화방',
    role,
    status: 'pending',
    createdBy: issuerUid,
    createdByName: 'nIcK',
    createdByEmail: 'heroyik@gmail.com',
    createdAt: serverTimestamp(),
  };
  if (note) inviteData.note = note;
  if (expiresAt !== undefined) inviteData.expiresAt = new Date(expiresAt);

  await Promise.all([
    setDoc(ticketRef(convoId, token), ticketData),
    setDoc(doc(getDb(), 'invitations', token), inviteData).catch((err: unknown) => {
      console.warn('[talk2nIcK] invitations 인덱스 생성 실패 (티켓은 정상 발급됨):', err);
    }),
  ]);

  const ticket: TicketDoc = {
    token,
    type: 'qr',
    role,
    status: 'pending',
    createdBy: issuerUid,
    createdAt: now,
    ...(note ? { note } : {}),
    ...(expiresAt !== undefined ? { expiresAt } : {}),
  };

  return { token, ticket, convoId, link: ticketLink(token, null) };
}

/** 미사용 티켓 취소(철회) — 방장/부방장만. 이력은 남긴다 (Rules가 pending → revoked만 허용) */
export async function revokeTicket(convoId: string, token: string): Promise<void> {
  await Promise.all([
    updateDoc(ticketRef(convoId, token), {
      status: 'revoked',
      revokedAt: serverTimestamp(),
    }),
    updateDoc(doc(getDb(), 'invitations', token), {
      status: 'revoked',
      revokedAt: serverTimestamp(),
    }).catch(() => {}),
  ]);
}

/** 티켓 히스토리 완전 삭제 — 단건 */
export async function deleteTicket(convoId: string, token: string): Promise<void> {
  const db = getDb();
  await Promise.all([
    deleteDoc(ticketRef(convoId, token)).catch(() => {}),
    deleteDoc(doc(db, 'invitations', token)).catch(() => {}),
  ]);
}

/** 티켓 히스토리 선택/전체 일괄 삭제 */
export async function deleteTicketsBulk(
  tickets: { convoId: string; token: string }[],
): Promise<void> {
  const db = getDb();
  // Firestore batch는 최대 500개까지 지원. 안전하게 100개 단위 청크로 나눈다.
  const CHUNK_SIZE = 100;
  for (let i = 0; i < tickets.length; i += CHUNK_SIZE) {
    const chunk = tickets.slice(i, i + CHUNK_SIZE);
    const batch = writeBatch(db);
    for (const item of chunk) {
      batch.delete(doc(db, 'invitations', item.token));
      if (item.convoId) {
        batch.delete(ticketRef(item.convoId, item.token));
      }
    }
    await batch.commit();
  }
}

// ── 목록(구독) ────────────────────────────────────────────────────────

export interface TicketListState {
  tickets: TicketDoc[];
  loading: boolean;
  error: unknown;
}

const EMPTY_TICKET_LIST: TicketListState = { tickets: [], loading: false, error: null };

/**
 * 방의 티켓 전체 구독 — 방장 시트 전용이라, 화면에 열려 있을 때만 리스너를 건다.
 * 정렬(미사용 → 사용됨 → 취소)은 core의 `sortTickets`가 담당한다.
 */
export function useTickets(convoId: string | null, enabled = true): TicketListState {
  const [state, setState] = useState<TicketListState>({
    tickets: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!convoId || !enabled) {
      setState(EMPTY_TICKET_LIST);
      return;
    }
    setState({ tickets: [], loading: true, error: null });

    const unsubscribe = onSnapshot(
      collection(getDb(), 'convos', convoId, 'tickets'),
      (snap) => {
        const tickets = snap.docs.map((d) => toTicketDoc(d.id, d.data()));
        setState({ tickets, loading: false, error: null });
      },
      (error: unknown) => {
        console.error('[talk2nIcK] 티켓 목록 구독 실패:', error);
        setState({ tickets: [], loading: false, error });
      },
    );
    return unsubscribe;
  }, [convoId, enabled]);

  return state;
}

// ── 진입(클레임) ──────────────────────────────────────────────────────

/**
 * 티켓을 읽는다. 아직 멤버가 아니면 Rules의 `get` 규칙이 **계정 이메일 일치**로만
 * 열어준다. 권한이 없으면 존재 여부를 숨기기 위해 "없는 티켓"과 같은 오류로 바꾼다.
 */
export async function readTicket(token: string): Promise<TicketDoc> {
  const parsed = parseToken(token);
  if (!parsed) throw new TicketError(TICKET_NOT_FOUND);
  let snap;
  try {
    snap = await getDoc(ticketRef(parsed.convoId, token));
  } catch (err) {
    console.warn('[talk2nIcK] convos/tickets 조회 실패, invitations 인덱스 폴백 시도:', err);
    try {
      const invSnap = await getDoc(doc(getDb(), 'invitations', token));
      if (invSnap.exists()) {
        return toTicketDoc(invSnap.id, invSnap.data() as Record<string, unknown>);
      }
    } catch (invErr) {
      console.error('[talk2nIcK] invitations 조회 실패:', invErr);
    }
    throw new TicketError(TICKET_NOT_FOUND);
  }
  if (!snap.exists()) {
    try {
      const invSnap = await getDoc(doc(getDb(), 'invitations', token));
      if (invSnap.exists()) {
        return toTicketDoc(invSnap.id, invSnap.data() as Record<string, unknown>);
      }
    } catch {
      // fallback fail silently
    }
    throw new TicketError(TICKET_NOT_FOUND);
  }
  return toTicketDoc(snap.id, snap.data() as Record<string, unknown>);
}

export interface ClaimTicketInput {
  introMessage?: string;
  introPhotoUrl?: string;
  nickname?: string;
  displayName?: string;
  /** 인앱 초대장 승인 또는 방장 발급 직결 시 승인 대기실을 건너뛰고 바로 참여 상태로 등록 */
  autoApprove?: boolean;
}

export interface ClaimedTicket {
  ticket: TicketDoc;
  /** 이미 멤버면 그대로, 신규면 방장/부방장이 부여한 역할 */
  role: TicketDoc['role'] | 'owner';
  /** 이번 호출이 실제로 방에 추가했는지 (재시도 시 false) */
  joined: boolean;
  convoId: string;
  /** 2단계 승인 상태 */
  admissionStatus: 'pending' | 'approved' | 'rejected';
}

/**
 * 진입 사전 검증. "들어가도 되는가"만 먼저 알려준다 (진입 화면 표시용).
 * 판정 자체는 core의 `checkClaim`이 한다.
 */
export async function precheckTicket(
  token: string,
  accountEmail: string | null,
): Promise<ClaimCheck> {
  let ticket: TicketDoc | null = null;
  try {
    ticket = await readTicket(token);
  } catch {
    ticket = null;
  }
  return checkClaim(ticket, accountEmail, Date.now());
}

/**
 * 티켓 사용 → 방 진입 (대기실 생성 또는 즉시 승인 진입).
 *
 * 멱등해야 한다: 오프라인에서 2단계 중 하나만 성공한 뒤 재시도하면,
 * 이미 쓰인 티켓을 다시 쓸 수 없어 복구가 불가능해진다. 그래서 각 단계 전에
 * "이미 끝났는지"를 확인하고, 있다면 건너뛴다.
 */
export async function claimTicket(
  token: string,
  myUid: string,
  accountEmail: string | null,
  intro?: ClaimTicketInput,
): Promise<ClaimedTicket> {
  const parsed = parseToken(token);
  if (!parsed) throw new TicketError(TICKET_NOT_FOUND);
  const convoId = parsed.convoId;
  const db = getDb();
  const memberRef = doc(db, 'convos', convoId, 'members', myUid);

  // 이미 멤버면 기존 상태 반환 (퇴실했다면 Rules가 막아 준다)
  const memberSnap = await getDoc(memberRef);
  if (memberSnap.exists()) {
    const data = memberSnap.data() as {
      leftAt?: Timestamp;
      role?: TicketDoc['role'] | 'owner';
      admissionStatus?: 'pending' | 'approved' | 'rejected';
    };
    if (!data.leftAt) {
      return {
        ticket: toTicketDoc(token, { status: 'used', usedBy: myUid, role: 'member' }),
        role: data.role ?? 'member',
        joined: false,
        convoId,
        admissionStatus: data.admissionStatus ?? 'approved',
      };
    }
    throw new TicketError('already-removed');
  }

  const ticket = await readTicket(token);
  const check = checkClaim(ticket, accountEmail, Date.now());
  if (!check.ok) throw new TicketError(check.reason);

  // 1) 티켓 소비 (pending → used)
  await updateDoc(ticketRef(convoId, token), {
    status: 'used',
    usedBy: myUid,
    usedAt: serverTimestamp(),
  });
  void updateDoc(doc(db, 'invitations', token), {
    status: 'used',
    usedBy: myUid,
    usedAt: serverTimestamp(),
  }).catch(() => {});

  const admissionStatus: 'pending' | 'approved' = intro?.autoApprove ? 'approved' : 'pending';

  // 2) 멤버 문서 생성 — autoApprove인 경우 'approved'로 바로 등록되어 즉시 대화 가능
  const memberData: Record<string, unknown> = {
    role: roleFromTicket(ticket),
    admissionStatus,
    introMessage: intro?.introMessage?.trim() || '',
    introPhotoUrl: intro?.introPhotoUrl || '',
    joinedViaTicket: token,
    requestedAt: serverTimestamp(),
    joinedAt: serverTimestamp(),
    lastReadAt: serverTimestamp(),
    unreadCount: 0,
    pinned: false,
    archived: false,
    translation: { mode: 'inherit', updatedAt: serverTimestamp() },
  };
  if (ticket.emailLower) memberData.userEmail = ticket.emailLower;
  else if (accountEmail) memberData.userEmail = accountEmail;
  if (intro?.nickname) memberData.nickname = intro.nickname.trim();
  if (intro?.displayName) memberData.displayName = intro.displayName.trim();
  if (admissionStatus === 'approved') {
    memberData.approvedAt = serverTimestamp();
    memberData.approvedBy = ticket.createdBy;
  }
  await setDoc(memberRef, memberData);

  // 3) memberIds에 본인 추가 — Rules의 addsOnlySelfAsMember()가 "나 하나만"을 보장한다.
  const convoRef = doc(db, 'convos', convoId);
  await updateDoc(convoRef, {
    memberIds: arrayUnion(myUid),
    updatedAt: serverTimestamp(),
  });

  return {
    ticket,
    role: roleFromTicket(ticket),
    joined: true,
    convoId,
    admissionStatus,
  };
}

/**
 * 대화방 입장 신청 시 신원 확인 사진을 Storage에 업로드 (800px 리사이즈 후 WebP/JPEG)
 */
export async function uploadIntroPhoto(
  uid: string,
  convoId: string,
  file: File | Blob,
): Promise<string> {
  const prepared = await prepareImage(file);
  const ext = prepared.mime === 'image/webp' ? 'webp' : 'jpg';
  const path = `users/${uid}/intro/${convoId}/photo_${Date.now()}.${ext}`;
  const storage = await getStorageClient();
  const storageRef = ref(storage, path);
  const snap = await uploadBytes(storageRef, prepared.blob, {
    contentType: prepared.mime,
  });
  return await getDownloadURL(snap.ref);
}

// ── 인앱 초대장 수신 (내 이메일로 온 초대장 실시간 구독) ─────────────────

function toInvitationDoc(id: string, data: Record<string, unknown>): InvitationDoc {
  const ms = (value: unknown): number | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'number') return value;
    const t = value as Timestamp;
    return typeof t?.toDate === 'function' ? t.toDate().getTime() : undefined;
  };
  return {
    token: id,
    convoId: (data.convoId as string) ?? '',
    convoTitle: typeof data.convoTitle === 'string' ? data.convoTitle : undefined,
    emailLower: (data.emailLower as string) ?? '',
    role: data.role === 'admin' ? 'admin' : 'member',
    status: (data.status as InvitationDoc['status']) ?? 'pending',
    note: typeof data.note === 'string' ? data.note : undefined,
    createdBy: (data.createdBy as string) ?? '',
    createdByName: typeof data.createdByName === 'string' ? data.createdByName : undefined,
    createdByEmail: typeof data.createdByEmail === 'string' ? data.createdByEmail : undefined,
    createdAt: ms(data.createdAt) ?? 0,
    expiresAt: ms(data.expiresAt),
    usedBy: typeof data.usedBy === 'string' ? data.usedBy : undefined,
    usedAt: ms(data.usedAt),
    revokedAt: ms(data.revokedAt),
  };
}

export interface MyInvitationsState {
  invitations: InvitationDoc[];
  loading: boolean;
  error: unknown;
}

/**
 * 로그인한 사용자의 Gmail 주소로 발급된 미사용 초대장 목록을 실시간 구독한다.
 * 온보딩(ID + PIN) 완료 후 인앱 알림 및 즉시 조인 카드를 표시하는 데 사용된다.
 */
export function useMyInvitations(accountEmail: string | null | undefined): MyInvitationsState {
  const [state, setState] = useState<MyInvitationsState>({
    invitations: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    const email = accountEmail?.trim().toLowerCase();
    if (!email) {
      setState({ invitations: [], loading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const q = query(
      collection(getDb(), 'invitations'),
      where('emailLower', '==', email),
      where('status', '==', 'pending'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const now = Date.now();
        const list = snap.docs
          .map((d) => toInvitationDoc(d.id, d.data()))
          .filter((inv) => !inv.expiresAt || inv.expiresAt > now);
        setState({ invitations: list, loading: false, error: null });
      },
      (error: unknown) => {
        console.error('[talk2nIcK] 인앱 초대장 목록 구독 실패:', error);
        setState({ invitations: [], loading: false, error });
      },
    );
    return unsubscribe;
  }, [accountEmail]);

  return state;
}

export interface AdminIssuedTicketsState {
  tickets: InvitationDoc[];
  loading: boolean;
  error: unknown;
}

/**
 * 관리자가 발급한 전체 초대장 목록을 실시간 구독한다.
 * 메인 대화 목록의 '발급한 티켓' 전용 탭에서 한눈에 모니터링할 때 사용된다.
 */
export function useAdminIssuedTickets(
  adminUid: string | null | undefined,
  enabled = true,
): AdminIssuedTicketsState {
  const [state, setState] = useState<AdminIssuedTicketsState>({
    tickets: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!adminUid || !enabled) {
      setState({ tickets: [], loading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const q = query(
      collection(getDb(), 'invitations'),
      where('createdBy', '==', adminUid),
      limit(100),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((d) => toInvitationDoc(d.id, d.data()))
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setState({ tickets: list, loading: false, error: null });
      },
      (error: unknown) => {
        console.error('[talk2nIcK] 관리자 발급 티켓 목록 구독 실패:', error);
        setState({ tickets: [], loading: false, error });
      },
    );
    return unsubscribe;
  }, [adminUid, enabled]);

  return state;
}

// ── 초대 요청 (일반 사용자 → Admin, 1.0.1) ───────────────────────────

export interface SendInviteRequestInput {
  uid: string;
  userEmail: string;
  userName: string;
  userHandle?: string;
  message: string;
  photoFile?: File | Blob;
}

/**
 * 일반 사용자가 Admin(nIcK)에게 초대 요청을 전송한다.
 * 선택적으로 사진을 Storage에 업로드한 후 /inviteRequests 문서를 생성한다.
 */
export async function sendInviteRequest(input: SendInviteRequestInput): Promise<string> {
  const db = getDb();
  let photoUrl = '';

  if (input.photoFile) {
    try {
      const prepared = await prepareImage(input.photoFile);
      const ext = prepared.mime === 'image/webp' ? 'webp' : 'jpg';
      const path = `users/${input.uid}/requests/request_${Date.now()}.${ext}`;
      const storage = await getStorageClient();
      const storageRef = ref(storage, path);
      const snap = await uploadBytes(storageRef, prepared.blob, { contentType: prepared.mime });
      photoUrl = await getDownloadURL(snap.ref);
    } catch (err) {
      console.warn('[talk2nIcK] 초대 요청 사진 업로드 실패 (메시지만 전송):', err);
    }
  }

  const reqRef = doc(collection(db, 'inviteRequests'));
  await setDoc(reqRef, {
    uid: input.uid,
    userEmail: normalizeEmail(input.userEmail),
    userName: input.userName || 'Guest',
    ...(input.userHandle ? { userHandle: input.userHandle } : {}),
    message: input.message.trim(),
    ...(photoUrl ? { photoUrl } : {}),
    status: 'pending',
    createdAt: serverTimestamp(),
  });

  return reqRef.id;
}

export interface IncomingInviteRequestsState {
  requests: InviteRequestDoc[];
  loading: boolean;
  error: unknown;
}

function toInviteRequestDoc(id: string, data: Record<string, unknown>): InviteRequestDoc {
  const ms = (value: unknown): number => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    const t = value as Timestamp;
    return typeof t?.toDate === 'function' ? t.toDate().getTime() : 0;
  };

  return {
    id,
    uid: (data.uid as string) ?? '',
    userEmail: (data.userEmail as string) ?? '',
    userName: (data.userName as string) ?? 'Guest',
    userHandle: data.userHandle as string | undefined,
    message: (data.message as string) ?? '',
    photoUrl: data.photoUrl as string | undefined,
    status: (data.status as InviteRequestDoc['status']) ?? 'pending',
    createdAt: ms(data.createdAt),
    handledAt: ms(data.handledAt),
    convoId: data.convoId as string | undefined,
  };
}

/**
 * Admin이 실시간으로 대기 중인 초대 요청 목록을 구독한다.
 */
export function useIncomingInviteRequests(isAdmin: boolean): IncomingInviteRequestsState {
  const [state, setState] = useState<IncomingInviteRequestsState>({
    requests: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!isAdmin) {
      setState({ requests: [], loading: false, error: null });
      return;
    }

    const q = query(
      collection(getDb(), 'inviteRequests'),
      where('status', '==', 'pending'),
      limit(50),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((d) => toInviteRequestDoc(d.id, d.data()))
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setState({ requests: list, loading: false, error: null });
      },
      (error: unknown) => {
        console.error('[talk2nIcK] 수신 초대 요청 구독 실패:', error);
        setState({ requests: [], loading: false, error });
      },
    );

    return unsubscribe;
  }, [isAdmin]);

  return state;
}

/**
 * Admin이 초대 요청을 거절한다.
 */
export async function rejectInviteRequest(adminUid: string, requestId: string): Promise<void> {
  const db = getDb();
  await updateDoc(doc(db, 'inviteRequests', requestId), {
    status: 'rejected',
    handledAt: serverTimestamp(),
    handledBy: adminUid,
  });
}

/**
 * Admin이 초대 요청을 승인하고 즉시 1:1 대화방을 개설한다.
 * 요청자의 introMessage를 바탕으로 멤버로 등록하며, 대화방이 즉시 양쪽 기기에 표시된다.
 */
export async function approveInviteRequest(
  adminUid: string,
  request: InviteRequestDoc,
): Promise<string> {
  const db = getDb();
  const convoId = ulid();

  // 1) convos 생성 (Admin 소유자 + 요청자 2인 포함)
  await setDoc(doc(db, 'convos', convoId), {
    type: 'dm',
    title: request.userName || '1:1 대화',
    createdBy: adminUid,
    memberIds: [adminUid, request.uid].sort(),
    adminIds: [adminUid],
    dmKey: convoId,
    settings: { disappearingTtlSeconds: 0, private: false },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessage: {
      text: request.message,
      senderId: request.uid,
      at: Date.now(),
    },
  });

  // 2) Admin 본인 멤버 문서 생성
  await setDoc(doc(db, 'convos', convoId, 'members', adminUid), {
    role: 'owner',
    joinedAt: serverTimestamp(),
    lastReadAt: serverTimestamp(),
    unreadCount: 0,
    pinned: false,
    archived: false,
    translation: { mode: 'inherit', updatedAt: serverTimestamp() },
  });

  // 3) 요청자 멤버 문서 생성 (admissionStatus: 'approved'로 즉시 승인)
  await setDoc(doc(db, 'convos', convoId, 'members', request.uid), {
    role: 'member',
    admissionStatus: 'approved',
    userEmail: request.userEmail,
    nickname: request.userName,
    displayName: request.userName,
    introMessage: request.message,
    introPhotoUrl: request.photoUrl || '',
    approvedAt: serverTimestamp(),
    approvedBy: adminUid,
    joinedAt: serverTimestamp(),
    lastReadAt: serverTimestamp(),
    unreadCount: 1,
    pinned: false,
    archived: false,
    translation: { mode: 'inherit', updatedAt: serverTimestamp() },
  });

  // 4) 요청자의 첫 인사말을 대화방의 첫 메시지로 자동 등록
  try {
    const msgRef = doc(collection(db, 'convos', convoId, 'messages'));
    await setDoc(msgRef, {
      senderId: request.uid,
      type: request.photoUrl ? 'media' : 'text',
      text: request.message,
      ...(request.photoUrl
        ? {
            mediaUrl: request.photoUrl,
            mediaType: 'image',
          }
        : {}),
      reactions: {},
      reactionCount: 0,
      unreadCount: 1,
      createdAt: serverTimestamp(),
    });
  } catch (msgErr) {
    console.warn('[talk2nIcK] 첫 메시지 생성 실패 (방 생성 및 승인은 계속 진행):', msgErr);
  }

  // 5) inviteRequests 상태를 approved로 업데이트
  await updateDoc(doc(db, 'inviteRequests', request.id), {
    status: 'approved',
    convoId,
    handledAt: serverTimestamp(),
    handledBy: adminUid,
  });

  return convoId;
}
